import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const shiftApi = createApi({
  reducerPath: 'shiftApi',
  baseQuery,
  tagTypes: ['Shift'],
  endpoints: (builder) => ({
    // Get all shifts
    getShifts: builder.query({
      query: (params = {}) => ({
        url: '/shifts',
        params,
      }),
      providesTags: ['Shift'],
    }),

    // Get single shift
    getShift: builder.query({
      query: (id) => `/shifts/${id}`,
      providesTags: (result, error, id) => [{ type: 'Shift', id }],
    }),

    // Create shift
    createShift: builder.mutation({
      query: (shiftData) => ({
        url: '/shifts',
        method: 'POST',
        body: shiftData,
      }),
      invalidatesTags: ['Shift'],
    }),

    // Update shift
    updateShift: builder.mutation({
      query: ({ id, ...shiftData }) => ({
        url: `/shifts/${id}`,
        method: 'PUT',
        body: shiftData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Shift', id }, 'Shift'],
    }),

    // Delete shift
    deleteShift: builder.mutation({
      query: (id) => ({
        url: `/shifts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Shift'],
    }),

    // Assign shift to employee
    assignShiftToEmployee: builder.mutation({
      query: ({ shiftId, employeeId }) => ({
        url: `/shifts/${shiftId}/assign`,
        method: 'POST',
        body: { employeeId },
      }),
      invalidatesTags: ['Shift'],
    }),

    // Unassign shift from employee
    unassignShiftFromEmployee: builder.mutation({
      query: ({ shiftId, employeeId }) => ({
        url: `/shifts/${shiftId}/unassign`,
        method: 'POST',
        body: { employeeId },
      }),
      invalidatesTags: ['Shift'],
    }),

    // Set shift as default
    setDefaultShift: builder.mutation({
      query: (shiftId) => ({
        url: `/shifts/${shiftId}/set-default`,
        method: 'POST',
      }),
      invalidatesTags: ['Shift'],
    }),

    // Get employees by shift
    getEmployeesByShift: builder.query({
      query: (shiftId) => `/shifts/${shiftId}/employees`,
      providesTags: ['Shift'],
    }),
  }),
});

export const {
  useGetShiftsQuery,
  useGetShiftQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useAssignShiftToEmployeeMutation,
  useUnassignShiftFromEmployeeMutation,
  useSetDefaultShiftMutation,
  useGetEmployeesByShiftQuery,
} = shiftApi;
